/**
 * Embedding Manager Component
 * 
 * Provides UI for managing document embeddings:
 * - Bulk embed documents
 * - View embedding statistics
 * - Re-embed documents
 * - Track progress
 * 
 * Usage:
 * <EmbeddingManager spaceId={spaceId} />
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ragService, type ProcessingProgress, type EmbeddingStats } from '@/services/ragService';
import { embeddingJobService, type EmbeddingJobRecord } from '@/services/embeddingJobService';
import { summarizeEmbeddingQueue } from '@/services/embeddingQueueSummary';
import { EMBEDDING_MODEL, EMBEDDING_DIMENSIONS } from '@/services/embeddingService';
import { useToast } from '@/hooks/use-toast';
import { knowledgeBaseService, type KnowledgeBaseItem } from '@/services/knowledgeBaseService';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Database, 
  Zap, 
  CheckCircle2, 
  AlertCircle, 
  RefreshCw,
  BarChart3
} from 'lucide-react';

interface EmbeddingManagerProps {
  spaceId?: string;
  documentIds?: string[];
}

export const EmbeddingManager: React.FC<EmbeddingManagerProps> = ({ 
  spaceId, 
  documentIds 
}) => {
  const [documents, setDocuments] = useState<KnowledgeBaseItem[]>([]);
  const [stats, setStats] = useState<EmbeddingStats | null>(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState<ProcessingProgress | null>(null);
  const [embeddingStatus, setEmbeddingStatus] = useState<Record<string, boolean>>({});
  const [queueJobs, setQueueJobs] = useState<EmbeddingJobRecord[]>([]);
  /** null until loaded. A set means we know which documents have a body. */
  const [contentIds, setContentIds] = useState<Set<string> | null>(null);
  const [loading, setLoading] = useState(true);
  const processingRef = useRef(false);
  const kickStartedRef = useRef(false);
  const { toast } = useToast();

  useEffect(() => {
    processingRef.current = processing;
  }, [processing]);

  const refreshQueueStatus = useCallback(async () => {
    try {
      const jobs = await embeddingJobService.getStatus();
      setQueueJobs(jobs);
    } catch {
      setQueueJobs([]);
    }
  }, []);

  const refreshStatsAndQueue = useCallback(async () => {
    const [statsData] = await Promise.all([
      ragService.getEmbeddingStats(),
      refreshQueueStatus(),
    ]);
    setStats(statsData);
  }, [refreshQueueStatus]);

  const refreshEmbeddingStatus = useCallback(async (docIds: string[]) => {
    if (docIds.length === 0) {
      setEmbeddingStatus({});
      return;
    }
    const statusMap = await ragService.getEmbeddingStatusForDocuments(docIds);
    setEmbeddingStatus(statusMap);
  }, []);

  // Load documents and stats
  useEffect(() => {
    loadData();
  }, [spaceId, documentIds]);

  // One wake per visit. The server pump continues after this page closes.
  useEffect(() => {
    if (loading || kickStartedRef.current) return;
    kickStartedRef.current = true;
    let cancelled = false;

    void (async () => {
      try {
        await embeddingJobService.backfillMissing();
      } catch (error) {
        if (!cancelled) {
          toast({
            title: 'Could not queue missing threads',
            description: error instanceof Error ? error.message : 'Thread backfill failed.',
            variant: 'destructive',
          });
        }
      }

      try {
        const jobs = await embeddingJobService.getStatus();
        if (!cancelled) setQueueJobs(jobs);
        const counts = summarizeEmbeddingQueue(jobs);
        if (counts.pending > 0 || counts.processing > 0) {
          await embeddingJobService.process(1);
        }
      } catch (error) {
        console.error('Embedding pump kick failed:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, toast]);

  const loadAllSummaries = async (spaceId?: string): Promise<KnowledgeBaseItem[]> => {
    const pageSize = 200;
    let offset = 0;
    const byId = new Map<string, KnowledgeBaseItem>();

    while (true) {
      const page = await knowledgeBaseService.getKnowledgeBaseSummaries({
        spaceId,
        allWorkspaces: !spaceId,
        limit: pageSize,
        offset,
      });
      for (const doc of page) {
        byId.set(doc.id, doc);
      }
      if (page.length < pageSize) break;
      offset += pageSize;
    }

    return Array.from(byId.values());
  };

  const loadData = async () => {
    setLoading(true);
    try {
      let docs: KnowledgeBaseItem[];
      if (documentIds) {
        docs = (await Promise.all(
          documentIds.map(id => knowledgeBaseService.getKnowledgeBaseItem(id))
        )).filter(Boolean) as KnowledgeBaseItem[];
        docs = docs.filter(doc => doc.content && doc.content.trim().length > 0);
      } else {
        docs = await loadAllSummaries(spaceId);
      }

      setDocuments(docs);

      try {
        const ids = await knowledgeBaseService.listDocumentIdsWithContent();
        setContentIds(new Set(ids));
      } catch (contentError) {
        console.error('Error loading document content flags:', contentError);
        setContentIds(null);
      }

      await refreshEmbeddingStatus(docs.map((doc) => doc.id));

      const statsData = await ragService.getEmbeddingStats();
      setStats(statsData);
      await refreshQueueStatus();
    } catch (error) {
      console.error('Error loading embedding data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatSkippedEmptyToast = (titles: string[]): { title: string; description: string } | null => {
    if (titles.length === 0) return null;
    const preview = titles.slice(0, 5).map((t) => `"${t}"`).join(', ');
    const more = titles.length > 5 ? ` (+${titles.length - 5} more)` : '';
    return {
      title:
        titles.length === 1
          ? 'Skipped empty document'
          : `Skipped ${titles.length} empty documents`,
      description:
        titles.length === 1
          ? `${preview} has no content and was skipped.`
          : `${preview}${more} have no content and were skipped.`,
    };
  };

  const documentHasContent = (id: string) => contentIds === null || contentIds.has(id);

  const handleEmbedAll = async () => {
    if (processing) return;

    const docsToEmbed = documents.filter(
      (doc) => documentHasContent(doc.id) && !embeddingStatus[doc.id]
    );
    if (docsToEmbed.length === 0) return;

    processingRef.current = true;
    setProcessing(true);

    try {
      const queued = await ragService.enqueueDocumentsForEmbedding(
        docsToEmbed.map((d) => d.id)
      );
      toast({
        title: 'Embedding queued',
        description: `Queued ${queued} document(s). Processing continues in the background until the queue is empty.`,
      });
      await refreshStatsAndQueue();
    } catch (error) {
      console.error('Error queueing documents:', error);
      toast({
        title: 'Could not queue embeddings',
        description: error instanceof Error ? error.message : 'Some documents could not be queued.',
        variant: 'destructive',
      });
    } finally {
      processingRef.current = false;
      setProcessing(false);
      setProgress(null);
    }
  };

  const handleReembedAll = async () => {
    if (
      processing ||
      !confirm(
        'Re-embed all documents with Qwen3-Embedding-8B (4000d halfvec)? Legacy GPT embeddings were cleared — this rebuilds the vector index via the server job queue.'
      )
    ) return;

    processingRef.current = true;
    setProcessing(true);
    let lastSkipped: string[] = [];

    try {
      const result = await ragService.embedDocuments(
        documents.map(d => d.id),
        (progress) => {
          if (progress.skippedTitles?.length) lastSkipped = progress.skippedTitles;
          setProgress(progress);
        },
        { forceReindex: true }
      );

      const skipped = result.skippedTitles?.length ? result.skippedTitles : lastSkipped;
      const skipToast = formatSkippedEmptyToast(skipped);
      if (skipToast) {
        toast({
          title: skipToast.title,
          description: skipToast.description,
        });
      }
      toast({
        title: 'Re-embed complete',
        description:
          skipped.length > 0
            ? `Finished with ${skipped.length} empty document(s) skipped.`
            : 'All documents with content were re-embedded.',
      });

      await refreshEmbeddingStatus(documents.map((d) => d.id));
      await refreshStatsAndQueue();
    } catch (error) {
      console.error('Error re-embedding documents:', error);
      const skipToast = formatSkippedEmptyToast(lastSkipped);
      if (skipToast) {
        toast({
          title: skipToast.title,
          description: skipToast.description,
        });
      }
      toast({
        title: 'Re-embed incomplete',
        description:
          error instanceof Error
            ? error.message
            : 'Some documents could not be re-embedded.',
        variant: 'destructive',
      });
      await refreshEmbeddingStatus(documents.map((d) => d.id));
      await refreshStatsAndQueue();
    } finally {
      processingRef.current = false;
      setProcessing(false);
      setProgress(null);
    }
  };

  const handleRetryChunkFailed = async () => {
    if (processing) return;
    processingRef.current = true;
    setProcessing(true);
    try {
      const reset = await embeddingJobService.retryChunkFailedJobs();
      toast({
        title: reset > 0 ? 'Jobs re-queued' : 'No matching jobs',
        description:
          reset > 0
            ? `${reset} job(s) with chunk-size errors were reset and will retry.`
            : 'No jobs with chunk-size errors were found.',
      });
      await refreshStatsAndQueue();
    } catch (error) {
      console.error('Error retrying chunk-failed jobs:', error);
      toast({
        title: 'Retry failed',
        description: error instanceof Error ? error.message : 'Could not reset jobs.',
        variant: 'destructive',
      });
    } finally {
      processingRef.current = false;
      setProcessing(false);
    }
  };

  const handleRetryStuck = async () => {
    if (processing) return;
    processingRef.current = true;
    setProcessing(true);
    try {
      await embeddingJobService.drainQueue({
        forceRecover: true,
        maxWaitMs: Math.min(3_600_000, Math.max(600_000, (queueCounts.pending + queueCounts.processing) * 90_000)),
        onProgress: (job) => {
          const p = job.progress as {
            documentTitle?: string;
            totalChunks?: number;
            processedChunks?: number;
          } | undefined;
          setProgress({
            documentId: job.target_id,
            documentTitle: p?.documentTitle || job.target_id,
            totalChunks: p?.totalChunks ?? 0,
            processedChunks: p?.processedChunks ?? 0,
            status: job.status === 'completed' ? 'completed' : 'processing',
            phase: 'processing',
          });
        },
      });
      await refreshEmbeddingStatus(documents.map((d) => d.id));
      await refreshStatsAndQueue();
    } catch (error) {
      console.error('Error retrying stuck jobs:', error);
      toast({
        title: 'Queue drain incomplete',
        description:
          error instanceof Error
            ? error.message
            : 'Some jobs may still be pending.',
        variant: 'destructive',
      });
      await refreshEmbeddingStatus(documents.map((d) => d.id));
      await refreshStatsAndQueue();
    } finally {
      processingRef.current = false;
      setProcessing(false);
      setProgress(null);
    }
  };

  const handleClearQueue = async () => {
    if (processing) return;
    if (
      !confirm(
        'Clear all pending, processing, and failed embedding jobs? You can re-embed from scratch afterward. Existing vectors are kept.'
      )
    ) {
      return;
    }
    processingRef.current = true;
    setProcessing(true);
    try {
      const { cancelled } = await embeddingJobService.clearQueue();
      await refreshStatsAndQueue();
      toast({
        title: 'Queue cleared',
        description: `Cancelled ${cancelled} job(s). Use Embed All or re-attach repos to start fresh.`,
      });
    } catch (error) {
      console.error('Error clearing embedding queue:', error);
      toast({
        title: 'Clear queue failed',
        description: error instanceof Error ? error.message : 'Could not cancel jobs.',
        variant: 'destructive',
      });
      await refreshStatsAndQueue();
    } finally {
      processingRef.current = false;
      setProcessing(false);
    }
  };

  const handleEmbedSingle = async (documentId: string) => {
    if (processing) return;

    processingRef.current = true;
    setProcessing(true);

    try {
      await ragService.embedDocument(documentId, (progress) => {
        setProgress(progress);
      });

      setEmbeddingStatus(prev => ({ ...prev, [documentId]: true }));
      await refreshStatsAndQueue();
    } catch (error) {
      console.error('Error embedding document:', error);
    } finally {
      processingRef.current = false;
      setProcessing(false);
      setProgress(null);
    }
  };

  const contentDocuments = documents.filter((doc) => documentHasContent(doc.id));
  const emptyCount = contentIds === null ? 0 : documents.length - contentDocuments.length;
  const contentCount = contentDocuments.length;
  const embeddedContentCount = contentDocuments.filter((doc) => embeddingStatus[doc.id]).length;
  const pendingCount = contentDocuments.filter((doc) => !embeddingStatus[doc.id]).length;
  const embeddedCount = embeddedContentCount;
  const totalCount = contentCount;

  const docTitleById = useMemo(
    () => Object.fromEntries(documents.map((d) => [d.id, d.title])),
    [documents]
  );

  const queueCounts = useMemo(() => summarizeEmbeddingQueue(queueJobs), [queueJobs]);
  const activeJobs = queueJobs.filter((job) => job.status === 'pending' || job.status === 'processing');
  const failedChunkJobs = queueJobs.filter(
    (job) => job.status === 'failed' && !!job.last_error && /chunk too large/i.test(job.last_error)
  );

  const documentJobCount = activeJobs.filter((j) => j.job_type === 'document').length;
  const threadJobCount = activeJobs.filter((j) => j.job_type === 'thread').length;

  const formatJobLine = (job: EmbeddingJobRecord): string => {
    const p = job.progress as {
      documentTitle?: string;
      threadTitle?: string;
      processedChunks?: number;
      totalChunks?: number;
    } | undefined;
    const typePrefix =
      job.job_type === 'thread'
        ? 'thread · '
        : job.job_type === 'repo'
          ? 'repo · '
          : '';
    const label =
      job.job_type === 'document'
        ? p?.documentTitle || docTitleById[job.target_id] || job.target_id
        : job.job_type === 'thread'
          ? p?.threadTitle || `thread ${job.target_id}`
          : job.target_id;
    const chunks =
      p?.totalChunks && p.totalChunks > 0
        ? ` · ${p.processedChunks ?? 0}/${p.totalChunks} chunks`
        : '';
    const err = job.last_error
      ? ` · ${job.last_error.length > 120 ? `${job.last_error.slice(0, 120)}…` : job.last_error}`
      : '';
    return `${typePrefix}${label} — ${job.status}${chunks}${err}`;
  };

  const isBatchProgress = Boolean(progress?.totalDocs && progress.totalDocs > 1);
  const progressPercent = progress
    ? isBatchProgress
      ? ((progress.completedDocs ?? 0) / (progress.totalDocs ?? 1)) * 100
      : progress.totalChunks > 0
        ? (progress.processedChunks / progress.totalChunks) * 100
        : 0
    : 0;

  const progressLabel = progress
    ? progress.phase === 'enqueuing'
      ? progress.documentTitle || 'Enqueuing documents…'
      : isBatchProgress
        ? progress.documentTitle
          ? `Document ${Math.min((progress.completedDocs ?? 0) + 1, progress.totalDocs ?? 0)} of ${progress.totalDocs}: ${progress.documentTitle}`
          : `Document ${progress.completedDocs ?? 0} of ${progress.totalDocs} complete`
        : progress.documentTitle || 'Processing…'
    : '';

  const progressDetail = progress
    ? isBatchProgress
      ? progress.phase === 'enqueuing'
        ? `${progress.totalDocs} documents`
        : progress.totalChunks > 0
          ? `${progress.completedDocs ?? 0}/${progress.totalDocs} docs · ${progress.processedChunks}/${progress.totalChunks} chunks`
          : `${progress.completedDocs ?? 0}/${progress.totalDocs} documents`
      : `${progress.processedChunks}/${progress.totalChunks} chunks`
    : '';

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Statistics Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Embedding Statistics
          </CardTitle>
          <CardDescription>
            Featherless {EMBEDDING_MODEL} ({EMBEDDING_DIMENSIONS}d) — server job queue
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Documents Embedded</p>
              <p className="text-2xl font-bold">{stats?.totalDocuments || 0}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Total Chunks</p>
              <p className="text-2xl font-bold">{stats?.totalChunks || 0}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Avg Chunks/Doc</p>
              <p className="text-2xl font-bold">
                {stats?.avgChunksPerDoc?.toFixed(1) || '0'}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Coverage</p>
              <p className="text-2xl font-bold">
                {totalCount > 0 ? Math.round((embeddedCount / totalCount) * 100) : 0}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Document Embeddings
          </CardTitle>
          <CardDescription>
            {embeddedCount} of {totalCount} documents with content embedded
            {emptyCount > 0 ? ` · ${emptyCount} empty skipped` : ''}
            {` · ${queueCounts.pending} pending · ${queueCounts.processing} processing · ${queueCounts.failed} failed`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Queue status */}
          {(queueCounts.pending > 0 || queueCounts.processing > 0 || queueCounts.failed > 0) && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="font-medium">
                  {queueCounts.pending} pending · {queueCounts.processing} processing · {queueCounts.failed} failed
                  {documentJobCount > 0 || threadJobCount > 0
                    ? ` (${documentJobCount} doc${documentJobCount === 1 ? '' : 's'}`
                      + (threadJobCount > 0 ? `, ${threadJobCount} thread${threadJobCount === 1 ? '' : 's'}` : '')
                      + ')'
                    : ''}
                </p>
                <div className="flex gap-2">
                  {failedChunkJobs.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRetryChunkFailed}
                      disabled={processing}
                    >
                      Retry chunk errors ({failedChunkJobs.length})
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRetryStuck}
                    disabled={processing}
                  >
                    Retry stuck
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={handleClearQueue}
                    disabled={processing}
                  >
                    Clear queue
                  </Button>
                </div>
              </div>
              <ul className="mt-2 text-xs text-muted-foreground space-y-1">
                {activeJobs.slice(0, 10).map((j) => (
                  <li key={j.id} className="truncate">
                    {formatJobLine(j)}
                  </li>
                ))}
                {activeJobs.length > 10 && (
                  <li>…and {activeJobs.length - 10} more</li>
                )}
              </ul>
            </div>
          )}

          {/* Progress Bar */}
          {processing && progress && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground truncate pr-2">
                  {progressLabel}
                </span>
                <span className="font-medium shrink-0">
                  {progressDetail}
                </span>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={handleEmbedAll}
              disabled={processing || pendingCount === 0}
              className="flex-1"
            >
              <Zap className="mr-2 h-4 w-4" />
              Embed All ({pendingCount} pending)
            </Button>
            <Button
              onClick={handleReembedAll}
              disabled={processing || totalCount === 0}
              variant="outline"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Re-embed All
            </Button>
          </div>

          {/* Document List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {documents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No documents found</p>
              </div>
            ) : (
              documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{doc.title}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!documentHasContent(doc.id) ? (
                      <Badge variant="outline" className="text-muted-foreground">
                        Empty
                      </Badge>
                    ) : embeddingStatus[doc.id] ? (
                      <Badge variant="default" className="gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Embedded
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEmbedSingle(doc.id)}
                        disabled={processing}
                      >
                        <Zap className="mr-1 h-3 w-3" />
                        Embed
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Database className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <div className="space-y-1 text-sm">
              <p className="font-medium">About Embeddings</p>
              <p className="text-muted-foreground">
                Embeddings use Featherless Qwen3-Embedding-8B (4000d halfvec, 32K context).
                Documents are processed on the server via a job queue. Processing continues in the background until the queue is empty, including after you leave this page.
                After the migration, re-embed all documents once to rebuild semantic search.
                API 413 errors mean a document chunk is too large — re-chunk or shorten content.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
